/* eslint-disable camelcase */
import React, { createContext } from 'react';
import { useFormik } from 'formik';
import Form from '../form-elements/Form';
import TextField from '../form-elements/TextField';
import TextArea from '../form-elements/TextArea';
import RadioGroup from '../form-elements/RadioGroup';
import Select from '../form-elements/Select';
import Required from '../form-elements/Required';
import Legend from '../form-elements/Legend';
import Picture from '../Picture';
import CheckboxGroup from '../form-elements/CheckboxGroup';
import LoadingSpinner from '../LoadingSpinner';
import SuccessModal from '../form-elements/SuccessModal';
import ErrorBoundary from '../ErrorBoundary';
import SidebarContact from './elements/SidebarContact';
import OpeningTimes from './elements/OpeningTimes';
import useDesignMyTripForm from './hooks/useDesignMyTripForm';

export const FormContext = createContext(null);

const DesignMyTripForm = () => {
  const {
    validationSchema,
    initialValues,
    disable,
    postForm,
    fields,
    handleCheckboxGroupChange,
    waiting,
    showSuccessDialog,
    setShowSuccessDialog,
    handleReCaptchaVerify,
  } = useDesignMyTripForm();

  const {
    handleBlur,
    touched,
    values,
    errors,
    setFieldValue,
    setTouched,
    setValues,
    submitForm,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async () => {
      const recaptchaToken = await handleReCaptchaVerify();

      const formData = {
        ...values,
        'g-recaptcha-response': recaptchaToken,
      };

      await postForm(formData, recaptchaToken, () => {
        setValues(initialValues);

        setTouched({});

        // post to data layer
        const {
          opt_in,
          first_name,
          last_name,
          phone,
          email,
          destinations,
          tell_more_dest,
          style,
          tell_more_style,
          accommodation,
          time,
          people,
          budget,
          current_page,
          referrer,
        } = values;

        // set email for DotDigital WBT
        window.dmPt('identify', email);

        const dataObj = {
          event: '2024_form_submit',
          data: {
            eventLabel: 'Design My Trip',
            formType: 'design',
            marketingConsent: opt_in,
            firstName: first_name,
            lastName: last_name,
            phone,
            email,
            places: destinations,
            plans: tell_more_dest,
            styles: style,
            enjoy_most: tell_more_style,
            accommodation_types: accommodation,
            when: time,
            travellerCount: people,
            budget,
            currentPage: current_page,
            referrer: referrer,
          },
        };

        window.dataLayer.push(dataObj);
      });
    },
    enableReinitialize: true,
  });

  const {
    first_name,
    last_name,
    phone,
    email,
    how_hear,
    tell_more_dest,
    style,
    accommodation,
    time,
    people,
    tell_more_style,
    tell_more_accomm,
    tell_more_people,
    destinations,
    budget,
    opt_in,
    honeypot,
    current_page,
    referrer,
    client_id,
  } = values;

  if (waiting) {
    return <LoadingSpinner />;
  } else {
    const { form, intro, thank_you } = fields;

    const {
      fieldset_destinations: {
        intro: destinationsIntro,
        fields: { destinations: destinationsMeta, tell_more: tellMoreDestMeta },
      },
      fieldset_style: {
        intro: styleIntro,
        fields: { style: styleMeta, tell_more: tellMoreStyleMeta },
      },
      fieldset_accommodation: {
        intro: accommodationIntro,
        fields: {
          accommodation: accommodationMeta,
          tell_more: tellMoreAccommMeta,
        },
      },
      fieldset_time: {
        intro: timeIntro,
        fields: { time: timeMeta },
      },
      fieldset_people: {
        intro: peopleIntro,
        fields: { people: peopleMeta, tell_more: tellMorePeopleMeta },
      },
      fieldset_budget: {
        intro: budgetIntro,
        fields: { budget: budgetMeta },
      },
      fieldset_enquiry: {
        intro: fieldsetEnquiryIntro,
        fields: {
          first_name: firstNameMeta,
          last_name: lastNameMeta,
          phone: phoneMeta,
          email: emailMeta,
          // purpose: purposeMeta,
          how_hear: howHearMeta,
          opt_in: optInMeta,
          gdpr: gdprMeta,
        },
      },
      submit: submitMeta,
    } = form;

    return (
      <FormContext.Provider value={fields.sidebar}>
        <section className="c-form c-form--contact o-wrapper">
          <ErrorBoundary>
            <div className="c-form__main">
              <div className="c-intro__content u-mb-14">
                <div className="c-intro__content__text">
                  <h1 className="c-intro__content__text__title o-after-line o-after-line--purple">
                    {intro.title}
                  </h1>

                  <p
                    className="o-text"
                    dangerouslySetInnerHTML={{ __html: intro.content }}
                  ></p>
                </div>
              </div>

              <Form
                className="c-form__body"
                onSubmit={(e) => {
                  e.preventDefault();
                  submitForm();
                }}
              >
                <section className="u-mb-16">
                  <h2 className="c-form__big-title u-color--purple">
                    {destinationsIntro.title}
                  </h2>

                  <p
                    className="o-text"
                    dangerouslySetInnerHTML={{
                      __html: destinationsIntro.content,
                    }}
                  ></p>

                  <p className="c-form__small u-mb-5">*Required Fields</p>

                  <fieldset>
                    <Legend>Destination plans</Legend>

                    <label className="u-a11y">
                      Honeypot
                      <input
                        name="honeypot"
                        type="hidden"
                        onChange={() => setFieldValue('honeypot', true)}
                        value={honeypot}
                      />
                    </label>

                    <input name="referrer" value={referrer} type="hidden" />
                    <input name="client_id" value={client_id} type="hidden" />
                    <input
                      name="current_page"
                      value={current_page}
                      type="hidden"
                    />

                    <div className="c-form__row c-form__row--single">
                      <div className="c-form__item c-form__item--destinations">
                        <CheckboxGroup
                          name="destinations"
                          options={destinationsMeta.options}
                          label=""
                          handleChange={(value) =>
                            handleCheckboxGroupChange(
                              value,
                              destinations,
                              setFieldValue,
                              'destinations'
                            )
                          }
                          selectedCheckboxes={destinations}
                          alignLeft
                        />
                      </div>
                    </div>

                    <div className="c-form__row c-form__row--single">
                      <div className="c-form__item c-form__item--enquiry u-mb-0">
                        <TextArea
                          label={
                            <>
                              {tellMoreDestMeta.label}
                              {tellMoreDestMeta.required && <Required />}
                            </>
                          }
                          name="enquiry"
                          placeholder={tellMoreDestMeta.placeholder}
                          onBlur={handleBlur}
                          handleChange={(value) =>
                            setFieldValue('tell_more_dest', value)
                          }
                          value={tell_more_dest}
                          validationError={
                            errors.tell_more_dest &&
                            touched[tellMoreDestMeta.id]
                          }
                          validationMessage={errors.tell_more_dest}
                        />
                      </div>
                    </div>
                  </fieldset>
                </section>

                <section className="u-mb-6">
                  <h2 className="c-form__big-title u-color--purple">
                    {styleIntro.title}
                  </h2>

                  <fieldset>
                    <Legend>Holiday Style</Legend>

                    <div className="c-form__row c-form__row--single">
                      <div className="c-form__item c-form__item--checkbox-group">
                        <CheckboxGroup
                          name={styleMeta.id}
                          options={styleMeta.options}
                          label=""
                          handleChange={(value) =>
                            handleCheckboxGroupChange(
                              value,
                              style,
                              setFieldValue,
                              'style'
                            )
                          }
                          selectedCheckboxes={style}
                        />
                      </div>
                    </div>

                    <div className="c-form__row c-form__row--single">
                      <div className="c-form__item c-form__item--enquiry u-mb-0">
                        <TextArea
                          label={
                            <>
                              {tellMoreStyleMeta.label}
                              {tellMoreStyleMeta.required && <Required />}
                            </>
                          }
                          id={tellMoreStyleMeta.id}
                          name={tellMoreStyleMeta.id}
                          placeholder={tellMoreStyleMeta.placeholder}
                          onBlur={handleBlur}
                          handleChange={(value) =>
                            setFieldValue('tell_more_style', value)
                          }
                          value={tell_more_style}
                          validationError={
                            errors.tell_more_style &&
                            touched[tellMoreStyleMeta.id]
                          }
                          validationMessage={errors.tell_more_dest}
                        />
                      </div>
                    </div>
                  </fieldset>
                </section>

                <section className="u-mb-6">
                  <h2 className="c-form__big-title u-color--purple">
                    {accommodationIntro.title}
                  </h2>

                  <fieldset>
                    <Legend>Accommodation</Legend>

                    <div className="c-form__row c-form__row--single">
                      <div className="c-form__item c-form__item--checkbox-group">
                        <CheckboxGroup
                          name={accommodationMeta.id}
                          options={accommodationMeta.options}
                          label=""
                          handleChange={(value) =>
                            handleCheckboxGroupChange(
                              value,
                              accommodation,
                              setFieldValue,
                              'accommodation'
                            )
                          }
                          selectedCheckboxes={accommodation}
                        />
                      </div>
                    </div>

                    <div className="c-form__row c-form__row--single">
                      <div className="c-form__item c-form__item--enquiry u-mb-0">
                        <TextArea
                          label={
                            <>
                              {tellMoreAccommMeta.label}
                              {tellMoreAccommMeta.required && <Required />}
                            </>
                          }
                          id={tellMoreAccommMeta.id}
                          name={tellMoreAccommMeta.id}
                          placeholder={tellMoreAccommMeta.placeholder}
                          onBlur={handleBlur}
                          handleChange={(value) =>
                            setFieldValue('tell_more_accomm', value)
                          }
                          value={tell_more_accomm}
                          validationError={
                            errors.tell_more_accomm &&
                            touched[tellMoreAccommMeta.id]
                          }
                          validationMessage={errors.tell_more_accomm}
                        />
                      </div>
                    </div>
                  </fieldset>
                </section>

                <section className="u-mb-6">
                  <h2 className="c-form__big-title u-color--purple">
                    {timeIntro.title}
                  </h2>

                  <fieldset>
                    <Legend>When travel?</Legend>

                    <div className="c-form__row c-form__row--single">
                      <div className="c-form__item c-form__item--enquiry u-mb-0">
                        <TextArea
                          label={
                            <>
                              {timeMeta.label}
                              {timeMeta.required && <Required />}
                            </>
                          }
                          id={timeMeta.id}
                          name={timeMeta.id}
                          placeholder={timeMeta.placeholder}
                          onBlur={handleBlur}
                          handleChange={(value) => setFieldValue('time', value)}
                          value={time}
                          validationError={errors.time && touched[timeMeta.id]}
                          validationMessage={errors.time}
                        />
                      </div>
                    </div>
                  </fieldset>
                </section>

                <section className="u-mb-6">
                  <h2 className="c-form__big-title u-color--purple">
                    {peopleIntro.title}
                  </h2>

                  <fieldset>
                    <Legend>{peopleIntro.title}</Legend>

                    <div className="c-form__row u-mb-2">
                      <div className="c-form__item">
                        <Select
                          options={peopleMeta.options.filter(
                            ({ disabled }) => !disabled
                          )}
                          value={people}
                          name={peopleMeta.id}
                          label={
                            <>
                              {peopleMeta.label}
                              {peopleMeta.required && <Required />}
                            </>
                          }
                          placeholderMessage={peopleMeta.placeholder_option}
                          handleChange={(value) =>
                            setFieldValue('people', value)
                          }
                        />
                      </div>
                    </div>

                    <div className="c-form__row c-form__row--single">
                      <div className="c-form__item c-form__item--enquiry u-mb-0">
                        <TextArea
                          label={
                            <>
                              {tellMorePeopleMeta.label}
                              {tellMorePeopleMeta.required && <Required />}
                            </>
                          }
                          id={tellMorePeopleMeta.id}
                          name={tellMorePeopleMeta.id}
                          placeholder={tellMorePeopleMeta.placeholder}
                          onBlur={handleBlur}
                          handleChange={(value) =>
                            setFieldValue('tell_more_people', value)
                          }
                          value={tell_more_people}
                          validationError={
                            errors.tell_more_people &&
                            touched[tellMorePeopleMeta.id]
                          }
                          validationMessage={errors.tell_more_people}
                        />
                      </div>
                    </div>
                  </fieldset>
                </section>

                <section className="u-mb-6">
                  <h2 className="c-form__big-title u-color--purple">
                    {budgetIntro.title}
                  </h2>

                  <p
                    className="o-text"
                    dangerouslySetInnerHTML={{
                      __html: budgetIntro.content,
                    }}
                  ></p>

                  <fieldset>
                    <Legend>Ideal budget per person</Legend>

                    <div className="c-form__row c-form__row--single">
                      <div className="c-form__item c-form__item--ideal-budget">
                        <RadioGroup
                          name="budget"
                          options={budgetMeta.options}
                          handleChange={(value) =>
                            setFieldValue('budget', value)
                          }
                          value={budget}
                          alignLeft
                        />
                      </div>
                    </div>
                  </fieldset>
                </section>

                <section>
                  <h2 className="c-form__big-title u-color--purple">
                    {fieldsetEnquiryIntro.title}
                  </h2>

                  <p
                    className="o-text"
                    dangerouslySetInnerHTML={{
                      __html: fieldsetEnquiryIntro.content,
                    }}
                  ></p>

                  <fieldset>
                    <Legend>Enquiry details</Legend>

                    <p className="c-form__small u-mb-5">*Required Fields</p>

                    <div className="c-form__row">
                      <div className="c-form__item">
                        <TextField
                          label={
                            <>
                              {firstNameMeta.label}
                              {firstNameMeta.required && <Required />}
                            </>
                          }
                          id={firstNameMeta.id}
                          name={firstNameMeta.id}
                          placeholder={firstNameMeta.placeholder}
                          onBlur={handleBlur}
                          handleChange={(value) =>
                            setFieldValue('first_name', value)
                          }
                          value={first_name}
                          validationError={
                            errors.first_name && touched[firstNameMeta.id]
                          }
                          validationMessage={errors.first_name}
                        />
                      </div>

                      <div className="c-form__item">
                        <TextField
                          label={
                            <>
                              {lastNameMeta.label}
                              {lastNameMeta.required && <Required />}
                            </>
                          }
                          id={lastNameMeta.id}
                          name={lastNameMeta.id}
                          placeholder={lastNameMeta.placeholder}
                          onBlur={handleBlur}
                          handleChange={(value) =>
                            setFieldValue('last_name', value)
                          }
                          value={last_name}
                          validationError={
                            errors.last_name && touched[lastNameMeta.id]
                          }
                          validationMessage={errors.last_name}
                        />
                      </div>
                    </div>

                    <div className="c-form__row">
                      <div className="c-form__item">
                        <TextField
                          label={
                            <>
                              {phoneMeta.label}
                              {phoneMeta.required && <Required />}
                            </>
                          }
                          id={phoneMeta.id}
                          name={phoneMeta.id}
                          type="tel"
                          placeholder={phoneMeta.placeholder}
                          onBlur={handleBlur}
                          handleChange={(value) =>
                            setFieldValue('phone', value)
                          }
                          value={phone}
                          validationError={
                            errors.phone && touched[phoneMeta.id]
                          }
                          validationMessage={errors.phone}
                        />
                      </div>

                      <div className="c-form__item">
                        <TextField
                          label={
                            <>
                              {emailMeta.label}
                              {emailMeta.required && <Required />}
                            </>
                          }
                          id={emailMeta.id}
                          name={emailMeta.id}
                          type="email"
                          placeholder={emailMeta.placeholder}
                          onBlur={handleBlur}
                          handleChange={(value) =>
                            setFieldValue('email', value)
                          }
                          value={email}
                          validationError={
                            errors.email && touched[emailMeta.id]
                          }
                          validationMessage={errors.email}
                        />
                      </div>
                    </div>

                    <div className="c-form__row u-mb-2">
                      {/* <div className="c-form__item">
                        <RadioGroup
                          name="purpose"
                          options={purposeMeta.options}
                          label={
                            <>
                              {purposeMeta.label}
                              {purposeMeta.required && <Required />}
                            </>
                          }
                          handleChange={(value) =>
                            setFieldValue('purpose', value)
                          }
                          value={purpose}
                        />
                      </div> */}

                      <div className="c-form__item">
                        <Select
                          options={howHearMeta.options.filter(
                            ({ disabled }) => !disabled
                          )}
                          value={how_hear}
                          name="heard"
                          label={
                            <>
                              {howHearMeta.label}
                              {howHearMeta.required && <Required />}
                            </>
                          }
                          placeholderMessage={howHearMeta.placeholder_option}
                          handleChange={(value) =>
                            setFieldValue('how_hear', value)
                          }
                        />
                      </div>
                    </div>

                    <div className="c-form__item c-form__item--opt-in u-mb-3">
                      <Legend>Opt in</Legend>

                      <label htmlFor="opt_in" className="c-form__checkbox">
                        <input
                          id="opt_in"
                          value="opt_in"
                          type="checkbox"
                          name="opt_in"
                          onChange={() => setFieldValue('opt_in', !opt_in)}
                          checked={opt_in}
                        />
                        <span></span>
                      </label>

                      <div>
                        <p className="u-weight--semibold u-mb-0">
                          {optInMeta.options[0].display}
                        </p>

                        <div
                          className="c-form__small u-mb-0"
                          dangerouslySetInnerHTML={{
                            __html: optInMeta.options[0].info,
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="c-form__item c-form__item--gdpr-no-checkbox u-mb-3">
                      <div>
                        <p className="u-weight--semibold u-mb-0">
                          {gdprMeta.display}
                        </p>

                        <p
                          className="c-form__small"
                          dangerouslySetInnerHTML={{
                            __html: gdprMeta.info,
                          }}
                        ></p>
                      </div>
                    </div>
                  </fieldset>
                </section>

                <div className="c-form__submit">
                  <button
                    type="submit"
                    className="o-btn"
                    disabled={disable(errors, values, initialValues)}
                  >
                    {submitMeta.label}
                  </button>
                </div>
              </Form>
            </div>
          </ErrorBoundary>

          <div className="c-form__sideinfo u-mb-10">
            <ErrorBoundary>
              <OpeningTimes />

              <SidebarContact />
            </ErrorBoundary>
          </div>
        </section>

        {showSuccessDialog && (
          <SuccessModal thank_you={thank_you} setShow={setShowSuccessDialog} />
        )}
      </FormContext.Provider>
    );
  }
};

export default DesignMyTripForm;
